import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";

import { useGetAllUserVerificationFarmTasks, useLocation, useTranslation } from "utils";

import { FarmResponse } from "models";

import { ErrorBoundary, Flex, Icon } from "components";

import { TaskOverviewDrawer } from "pages/app/farm/components/TaskOverviewDrawer";

type Task = { isLate: boolean; isComingUp: boolean; isToBeCompleted: boolean; deadline: Date; farmId: string; taskState: string; taskText: string };

type TaskMap = {
  [taskId: string]: Task;
};

type ActiveTasks = Task & { taskId: string; order: number };

export const TaskStatusFooter = ({ farm }: { farm: FarmResponse }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const taskDrawerParams: string | null = params.get("openTaskDrawer");
  const { t } = useTranslation("dashboard");

  const { data: farmTasks, isLoading: isLoadingFarmTasks, refetch: refetchFarmTasks } = useGetAllUserVerificationFarmTasks(farm.id);

  const [showTaskOverviewDrawer, setShowTaskOverviewDrawer] = useState(false);

  // Get the status of each task
  const tasksStatus = useMemo(
    () =>
      farmTasks?.reduce<TaskMap>((acc, task) => {
        if (task.completedAt) return acc; // skip completed tasks

        const daysToDeadline = moment(task.deadline).startOf("day").diff(moment().startOf("day"), "days");

        const status: Task = {
          isLate: daysToDeadline < 0,
          isComingUp: daysToDeadline >= 0 && daysToDeadline <= 3,
          isToBeCompleted: daysToDeadline > 3,
          deadline: task.deadline,
          farmId: task.farmId,
          taskState: "",
          taskText: "",
        };

        if (status.isLate) {
          acc[task.id] = {
            ...status,
            taskState: "is-late",
            taskText: t("taskStatusFooter.hasLateText"),
          };
        } else if (status.isComingUp && status.deadline) {
          acc[task.id] = {
            ...status,
            taskState: "is-coming-up",
            taskText: t("taskStatusFooter.hasComingUpText", { days: daysToDeadline }),
          };
        } else if (status.isToBeCompleted) {
          acc[task.id] = {
            ...status,
            taskState: "is-to-be-completed",
            taskText: t("taskStatusFooter.hasNewText"),
          };
        }

        return acc;
      }, {}),

    [farmTasks, t]
  );

  // Prioritise tasks: isLate > isComingUp > isToBeCompleted
  const activeTasks: ActiveTasks[] = useMemo(() => {
    if (!tasksStatus) return [];
    return Object.entries(tasksStatus)
      .map(([taskId, task]) => ({
        taskId,
        order: task.isLate ? 0 : task.isComingUp ? 1 : 2,
        ...task,
      }))
      .sort((a, b) => a.order - b.order);
  }, [tasksStatus]);

  const activeCount = activeTasks.length;

  // Handle the button link in the task notification email
  useEffect(() => {
    if (taskDrawerParams === farm.id) {
      const hasMatchingTask = activeTasks.some((task) => task.farmId === taskDrawerParams);
      if (hasMatchingTask) {
        setShowTaskOverviewDrawer(true);
      }
    }
  }, [farm.id, activeTasks, taskDrawerParams]);

  return (
    <>
      {activeCount > 0 && (
        <Flex
          className={`cta ${activeTasks[0].taskState}`}
          data-testid={`home-page-task-status-footer-${activeTasks[0].taskState}`}
          justify="space-between"
          align="center"
          onClick={() => setShowTaskOverviewDrawer(true)}
        >
          <Flex data-testid={`home-page-task-status-footer-${activeCount}`}>
            <Icon iconName="tabler/files" className="icon inline filled" />
            <span className="paragraph-m-bold margin-left-50">{t("taskStatusFooter.pendingTasks", { activeCount: activeCount, count: activeCount })}</span>
          </Flex>
          <Flex>
            <span className="paragraph-m-bold margin-right-50">{activeTasks[0].taskText}</span>
            <Icon iconName="tabler/arrow-narrow-right" className="icon inline filled" />
          </Flex>
        </Flex>
      )}
      <ErrorBoundary>
        <TaskOverviewDrawer
          farmTasks={farmTasks ?? []}
          visible={showTaskOverviewDrawer}
          onClose={() => setShowTaskOverviewDrawer(false)}
          onDataChange={refetchFarmTasks}
          loading={isLoadingFarmTasks}
        />
      </ErrorBoundary>
    </>
  );
};
